import React, { useState, useEffect } from 'react';
import './DigitalFlyerPopup.css';
import { fetchLiveDeals, addTask } from '../services/taskService';

function DigitalFlyerPopup({ onClose }) {
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);

  // Modal logic for picking a date
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isAddingTask, setIsAddingTask] = useState(false);

  useEffect(() => {
    loadDeals();
  }, []);

  const loadDeals = async () => {
    try {
      const data = await fetchLiveDeals();
      if (data && data.flyer_pages) {
        setPages(data.flyer_pages);
      }
    } catch (e) {
      console.error("Failed to load live deals:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const initiateAddTask = (deal) => {
    setSelectedDeal(deal);
    setShowDatePicker(true);
  };

  const handleConfirmAdd = async () => {
    if (!selectedDeal) return;
    setIsAddingTask(true);
    try {
      const taskText = `Buy ${selectedDeal.item_name} at ${selectedDeal.store} (${selectedDeal.price})`;
      await addTask(taskText, selectedDate, false, 'Shopping');
      setShowDatePicker(false);
      alert(`Added ${selectedDeal.item_name} to your tasks!`);
    } catch (error) {
      alert('Failed to add task.');
    } finally {
      setIsAddingTask(false);
    }
  };

  if (loading) {
    return (
      <div className="flyer-overlay">
        <div className="flyer-modal flyer-modal--center">
          <div className="flyer-spinner" />
          <p className="flyer-loading-text">Loading this week's deals…</p>
        </div>
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="flyer-overlay" onClick={onClose}>
        <div className="flyer-modal flyer-modal--center" onClick={e => e.stopPropagation()}>
          <button className="flyer-close-btn flyer-close-btn--float" onClick={onClose} aria-label="Close">&times;</button>
          <div className="flyer-empty-icon">🛒</div>
          <h2 className="flyer-empty-title">No deals right now</h2>
          <p className="flyer-empty-sub">Check back soon — fresh deals drop every week.</p>
        </div>
      </div>
    );
  }

  const currentDealPage = pages[currentPage];

  return (
    <div className="flyer-overlay" onClick={onClose}>
      <div className="flyer-modal" onClick={e => e.stopPropagation()}>

        {/* Hero header — store photo sits behind a readable gradient */}
        <div
          className="flyer-hero"
          style={currentDealPage.bg_image ? { backgroundImage: `url(${currentDealPage.bg_image})` } : undefined}
        >
          <div className="flyer-hero__scrim" />
          <div className="flyer-hero__topbar">
            <span className="flyer-live-badge">
              <span className="flyer-live-dot" />
              Available now
            </span>
            <button className="flyer-close-btn" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="flyer-hero__headline">
            <span className="flyer-eyebrow">This week's deals</span>
            <h2 className="flyer-store-name">{currentDealPage.title}</h2>
            <p className="flyer-hero__sub">Tap a deal to drop it straight onto your shopping list.</p>
          </div>
        </div>

        {/* Deals */}
        <div className="flyer-body">
          <div className="deals-grid">
            {currentDealPage.deals.map((item, index) => (
              <div key={index} className="deal-card">
                <div className="deal-card__media">
                  {item.image ? (
                    <img src={item.image} alt={item.item_name} className="deal-card__img" />
                  ) : (
                    <span className="deal-card__emoji">🛒</span>
                  )}
                </div>
                <div className="deal-card__body">
                  <span className="deal-card__name">{item.item_name}</span>
                  <span className="deal-card__store">{item.store}</span>
                  <span className="deal-card__price">{item.price}</span>
                </div>
                <button className="deal-card__btn" onClick={() => initiateAddTask(item)}>
                  + Add to list
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination */}
        {pages.length > 1 && (
          <div className="flyer-footer">
            <button className="nav-arrow" onClick={handlePrev} disabled={currentPage === 0}>
              ← Prev
            </button>
            <div className="flyer-dots">
              {pages.map((_, i) => (
                <span
                  key={i}
                  className={`flyer-dot${i === currentPage ? ' flyer-dot--active' : ''}`}
                  onClick={() => setCurrentPage(i)}
                />
              ))}
            </div>
            <button className="nav-arrow" onClick={handleNext} disabled={currentPage === pages.length - 1}>
              Next →
            </button>
          </div>
        )}

        {/* Date Picker Modal */}
        {showDatePicker && (
          <div className="flyer-datepicker-overlay" onClick={() => setShowDatePicker(false)}>
            <div className="flyer-datepicker" onClick={e => e.stopPropagation()}>
              <h3 className="flyer-datepicker__title">When to shop?</h3>
              <p className="flyer-datepicker__sub">
                Add <strong>{selectedDeal?.item_name}</strong> to your tasks.
              </p>
              <input
                type="date"
                className="flyer-datepicker__input"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
              />
              <div className="flyer-datepicker__actions">
                <button className="flyer-btn flyer-btn--ghost" onClick={() => setShowDatePicker(false)}>
                  Cancel
                </button>
                <button className="flyer-btn flyer-btn--primary" onClick={handleConfirmAdd} disabled={isAddingTask}>
                  {isAddingTask ? 'Adding…' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DigitalFlyerPopup;
